import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { APP_CONFIG } from "@/config/appConfig";

export interface ApiCallOptions {
  method?: "get" | "post" | "put" | "delete" | "patch";
  endpoint: string;
  data?: FormData | Record<string, unknown>;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  responseType?: AxiosRequestConfig["responseType"];
}

export interface ApiErrorData {
  error: string;
  message: string;
  path: string;
  status: number;
  timestamp: string;
}

type UnauthorizedHandler = (() => void) | null;

let inspectorPortalUnauthorizedHandler: UnauthorizedHandler = null;

export function setInspectorPortalUnauthorizedHandler(
  handler: UnauthorizedHandler,
) {
  inspectorPortalUnauthorizedHandler = handler;
}

export const createAxiosInstance = ({
  config,
}: {
  config?: AxiosRequestConfig;
} = {}): AxiosInstance => {
  const axiosInstance = axios.create({
    withCredentials: true,
    ...config,
  });

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        inspectorPortalUnauthorizedHandler?.();
      }

      return Promise.reject(error);
    },
  );

  return axiosInstance;
};

export const apiCall = async <T = unknown>(
  options: ApiCallOptions,
): Promise<T> => {
  const {
    method = "get",
    endpoint,
    data,
    params,
    headers,
    responseType,
  } = options;
  const axiosInstance = createAxiosInstance({
    config: { baseURL: APP_CONFIG.SERVER_BASE_URL },
  });

  const response = await axiosInstance.request<T>({
    url: endpoint,
    method,
    data,
    params,
    headers,
    responseType,
  });

  return response.data;
};

export const handleApiError = (err: unknown): ApiErrorData => {
  if (axios.isAxiosError(err)) {
    const typedError = err as AxiosError;

    return {
      error: typedError.code ?? "AxiosError",
      message: typedError.message,
      path: typedError.request?.path ?? "",
      status: typedError.response?.status ?? 0,
      timestamp: new Date().toISOString(),
    };
  }

  return {
    error: "GeneralError",
    message: err instanceof Error ? err.message : "Unknown error",
    path: "",
    status: 0,
    timestamp: new Date().toISOString(),
  };
};
